import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MessageCircle, RefreshCw, Send } from "lucide-react";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { fetchInstituteSettings } from "../../../Apis/InstituteSettings-api";
import {
  fetchWhatsAppHistory,
  fetchWhatsAppTemplates,
  searchLeadsForWhatsApp,
  searchStudentsForWhatsApp,
  sendWhatsAppMessages,
} from "../../../Apis/WhatsApp-api";

function extractRows(payload) {
  const raw = payload?.data ?? payload;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

function studentLabel(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  const phone = row.phone || row.alternate_phone || "";
  const id = row.student_id || row.organization_ems_student_id;
  return `${name || "Student"} (${id})${phone ? ` · ${phone}` : ""}`;
}

function leadLabel(row) {
  const name = row.person_full_name || "Lead";
  const phone = row.phone || row.alternate_phone || "";
  const id = row.organization_ems_lead_id;
  return `${name} (#${id})${phone ? ` · ${phone}` : ""}`;
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function SendWhatsAppPage() {
  const { userData } = useAuthStore();
  const organizationId = userData?.organization?.organization_id;

  const [whatsappEnabled, setWhatsappEnabled] = React.useState(null);
  const [templates, setTemplates] = React.useState([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [languageCode, setLanguageCode] = React.useState("en_US");

  const [studentOptions, setStudentOptions] = React.useState([]);
  const [leadOptions, setLeadOptions] = React.useState([]);
  const [selectedStudents, setSelectedStudents] = React.useState([]);
  const [selectedLeads, setSelectedLeads] = React.useState([]);
  const [manualPhones, setManualPhones] = React.useState("");
  const [studentsLoading, setStudentsLoading] = React.useState(false);
  const [leadsLoading, setLeadsLoading] = React.useState(false);

  const [sending, setSending] = React.useState(false);
  const [sendResults, setSendResults] = React.useState(null);

  const [history, setHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const loadInstituteGate = React.useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await fetchInstituteSettings(organizationId);
      const enabled = Boolean(res?.data?.whatsapp_notifications_enabled);
      setWhatsappEnabled(enabled);
    } catch {
      setWhatsappEnabled(false);
    }
  }, [organizationId]);

  const loadTemplates = React.useCallback(async () => {
    if (!organizationId || whatsappEnabled === false) return;
    setTemplatesLoading(true);
    try {
      const res = await fetchWhatsAppTemplates(organizationId);
      if (!res?.success) {
        toast.error(res?.message || "Failed to load templates");
        setTemplates([]);
        return;
      }
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load WhatsApp templates"
      );
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [organizationId, whatsappEnabled]);

  const loadHistory = React.useCallback(async () => {
    if (!organizationId) return;
    setHistoryLoading(true);
    try {
      const res = await fetchWhatsAppHistory(organizationId, 20, 1);
      const rows = extractRows(res);
      setHistory(rows);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [organizationId]);

  React.useEffect(() => {
    loadInstituteGate();
  }, [loadInstituteGate]);

  React.useEffect(() => {
    if (whatsappEnabled) {
      loadTemplates();
      loadHistory();
    }
  }, [whatsappEnabled, loadTemplates, loadHistory]);

  React.useEffect(() => {
    if (selectedTemplate?.language) {
      setLanguageCode(selectedTemplate.language);
    }
  }, [selectedTemplate]);

  const searchStudents = React.useCallback(
    async (query) => {
      if (!organizationId) return;
      setStudentsLoading(true);
      try {
        const res = await searchStudentsForWhatsApp(organizationId, query);
        setStudentOptions(extractRows(res));
      } catch {
        setStudentOptions([]);
      } finally {
        setStudentsLoading(false);
      }
    },
    [organizationId]
  );

  const searchLeads = React.useCallback(
    async (query) => {
      if (!organizationId) return;
      setLeadsLoading(true);
      try {
        const res = await searchLeadsForWhatsApp(organizationId, query);
        setLeadOptions(extractRows(res));
      } catch {
        setLeadOptions([]);
      } finally {
        setLeadsLoading(false);
      }
    },
    [organizationId]
  );

  React.useEffect(() => {
    if (whatsappEnabled) {
      searchStudents("");
      searchLeads("");
    }
  }, [whatsappEnabled, searchStudents, searchLeads]);

  const handleSend = async () => {
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }
    if (!selectedTemplate?.name) {
      toast.error("Select a WhatsApp template");
      return;
    }

    const recipients = [];
    selectedStudents.forEach((s) => {
      recipients.push({
        type: "student",
        id: s.organization_ems_student_id,
      });
    });
    selectedLeads.forEach((l) => {
      recipients.push({
        type: "lead",
        id: l.organization_ems_lead_id,
      });
    });
    String(manualPhones || "")
      .split(/[\n,;]+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((phone) => {
        recipients.push({ type: "manual", phone });
      });

    if (!recipients.length) {
      toast.error("Add at least one recipient (student, lead, or phone)");
      return;
    }

    setSending(true);
    setSendResults(null);
    try {
      const res = await sendWhatsAppMessages({
        organization_id: organizationId,
        template_name: selectedTemplate.name,
        language_code: languageCode || "en_US",
        recipients,
        components: [],
      });
      setSendResults(res?.data || null);
      if (res?.success) {
        toast.success(res.message || "Messages sent");
      } else {
        toast.error(res?.message || "Some messages failed");
      }
      setSelectedStudents([]);
      setSelectedLeads([]);
      setManualPhones("");
      loadHistory();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send WhatsApp messages"
      );
      if (error?.response?.data?.data) {
        setSendResults(error.response.data.data);
      }
    } finally {
      setSending(false);
    }
  };

  if (!organizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Select an active organization to send WhatsApp messages.</Alert>
      </Box>
    );
  }

  if (whatsappEnabled === null) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!whatsappEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          WhatsApp notifications are disabled for this organization. Enable{" "}
          <strong>WhatsApp notifications</strong> in Institute Settings, then
          return here to send template messages.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <MessageCircle size={22} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Send WhatsApp
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pick an approved Meta template and send it to students, leads, or any
            phone number.
          </Typography>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Autocomplete
              sx={{ flex: 2 }}
              options={templates}
              loading={templatesLoading}
              value={selectedTemplate}
              onChange={(_, value) => setSelectedTemplate(value)}
              getOptionLabel={(opt) =>
                opt?.name
                  ? `${opt.name}${opt.language ? ` (${opt.language})` : ""}`
                  : ""
              }
              isOptionEqualToValue={(a, b) =>
                a?.name === b?.name && a?.language === b?.language
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Template"
                  placeholder="Select approved template"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templatesLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              sx={{ flex: 1 }}
              select
              label="Language"
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
            >
              {[
                ...new Set([
                  languageCode,
                  selectedTemplate?.language,
                  "en_US",
                  "en",
                ].filter(Boolean)),
              ].map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={() => {
                loadTemplates();
                loadHistory();
              }}
              sx={{ alignSelf: { md: "center" }, whiteSpace: "nowrap" }}
            >
              Refresh
            </Button>
          </Stack>

          <Autocomplete
            multiple
            options={studentOptions}
            loading={studentsLoading}
            value={selectedStudents}
            onChange={(_, value) => setSelectedStudents(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") searchStudents(value);
            }}
            getOptionLabel={studentLabel}
            isOptionEqualToValue={(a, b) =>
              a?.organization_ems_student_id === b?.organization_ems_student_id
            }
            filterOptions={(x) => x}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.organization_ems_student_id}
                  label={studentLabel(option)}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Students"
                placeholder="Search students"
              />
            )}
          />

          <Autocomplete
            multiple
            options={leadOptions}
            loading={leadsLoading}
            value={selectedLeads}
            onChange={(_, value) => setSelectedLeads(value)}
            onInputChange={(_, value, reason) => {
              if (reason === "input") searchLeads(value);
            }}
            getOptionLabel={leadLabel}
            isOptionEqualToValue={(a, b) =>
              a?.organization_ems_lead_id === b?.organization_ems_lead_id
            }
            filterOptions={(x) => x}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.organization_ems_lead_id}
                  label={leadLabel(option)}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Leads" placeholder="Search leads" />
            )}
          />

          <TextField
            label="Manual phone numbers"
            placeholder="One per line or comma-separated (e.g. 919216552165)"
            value={manualPhones}
            onChange={(e) => setManualPhones(e.target.value)}
            multiline
            minRows={2}
            helperText="Use country code without +. Test numbers must be added in Meta API Setup."
          />

          <Box>
            <Button
              variant="contained"
              startIcon={
                sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send size={16} />
                )
              }
              disabled={sending || !selectedTemplate}
              onClick={handleSend}
            >
              {sending ? "Sending…" : "Send template"}
            </Button>
          </Box>

          {sendResults?.results?.length ? (
            <Alert
              severity={sendResults.failed ? "warning" : "success"}
              sx={{ mt: 1 }}
            >
              Sent {sendResults.sent ?? 0}, failed {sendResults.failed ?? 0}.
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                {sendResults.results.map((r, idx) => (
                  <li key={`${r.phone}-${idx}`}>
                    {r.recipient_name || r.phone} — {r.success ? "OK" : r.message}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Typography variant="h6" fontWeight={600}>
            Recent sends
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshCw size={14} />}
            onClick={loadHistory}
            disabled={historyLoading}
          >
            Refresh
          </Button>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        {historyLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : history.length === 0 ? (
          <Typography color="text.secondary">No messages sent yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((row) => (
                <TableRow key={row.whatsapp_message_log_id}>
                  <TableCell>{formatDate(row.created_at)}</TableCell>
                  <TableCell>
                    {row.recipient_name || row.recipient_type}
                    {row.recipient_id ? ` #${row.recipient_id}` : ""}
                  </TableCell>
                  <TableCell>{row.recipient_phone}</TableCell>
                  <TableCell>
                    {row.template_name}
                    {row.language_code ? ` (${row.language_code})` : ""}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={row.status === "sent" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
